/**
 * UsersTab — Agent access control management (super-admins, per-agent admins/users, Entra roles).
 * Includes role simulation (mock profiles) for testing RBAC locally.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import {
  RefreshCw, Plus, CheckCircle, XCircle, Users, Shield, UserCheck,
  Mail, Globe, ChevronDown, MapPin, Info, Loader2, X, Trash2, Briefcase,
  AlertTriangle, FlaskConical, Check, Save
} from 'lucide-react';
import type { AgentAccessData, AgentUser } from './types';
import { API_BASE, authFetch, getAgentColor, getAgentDisplayName } from './constants';
import {
  MOCK_PROFILES,
  getActiveMockProfile,
  enableMockProfile,
  disableMockProfile,
  isMockProfileEnabled,
  type MockProfile
} from '../../utils/mockProfiles';

interface UsersTabProps {
  agentAccess: AgentAccessData | null;
  loading: boolean;
  onRefresh: () => void;
  selectedEnvironment: string;
}

const UsersTab: React.FC<UsersTabProps> = ({ agentAccess, loading, onRefresh, selectedEnvironment }) => {
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [addEmailInput, setAddEmailInput] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [addRole, setAddRole] = useState<'user' | 'admin'>('user');
  const [bulkAgentId, setBulkAgentId] = useState<string | null>(null);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkRole, setBulkRole] = useState<'user' | 'admin'>('user');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'role' | 'source' | 'date'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Job code management state
  const [newJobCode, setNewJobCode] = useState('');
  const [newJobCodeTitle, setNewJobCodeTitle] = useState('');
  const [jobCodeLoading, setJobCodeLoading] = useState(false);
  // Bulk job-code editor: one row per code, open for at most one agent at a time.
  const [bulkJobCodeAgent, setBulkJobCodeAgent] = useState<string | null>(null);
  const [bulkJobCodeRows, setBulkJobCodeRows] = useState<{ code: string; title: string }[]>([{ code: '', title: '' }]);
  const bulkJobCodeCount = bulkJobCodeRows.filter(r => r.code.trim()).length;

  // Visibility matrix state
  const [matrixExpanded, setMatrixExpanded] = useState(true);
  const [matrixDirty, setMatrixDirty] = useState(false);
  const [matrixSaving, setMatrixSaving] = useState(false);
  // Pending overrides: key = "role:agentId" or "__default__:agentId", value = granted boolean
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, { role: string; agentId: string; granted: boolean; isDefault: boolean }>>({});

  // Role simulation state
  const [simExpanded, setSimExpanded] = useState(false);
  const [activeSimProfile, setActiveSimProfile] = useState<MockProfile | null>(() => getActiveMockProfile());
  const isSimEnabled = useMemo(() => isMockProfileEnabled(), [activeSimProfile]);

  const isLocalDev = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local');
  }, []);

  const handleSimProfileSelect = useCallback((profileId: string) => {
    if (activeSimProfile?.id === profileId) {
      disableMockProfile();
      setActiveSimProfile(null);
    } else {
      enableMockProfile(profileId);
      setActiveSimProfile(MOCK_PROFILES[profileId]);
    }
  }, [activeSimProfile]);

  const handleSimDisable = useCallback(() => {
    disableMockProfile();
    setActiveSimProfile(null);
  }, []);

  const handleMatrixToggle = useCallback((role: string, agentId: string, currentlyGranted: boolean, isDefault: boolean) => {
    const key = `${role}:${agentId}`;
    setPendingOverrides(prev => {
      const next = { ...prev };
      next[key] = { role, agentId, granted: !currentlyGranted, isDefault };
      return next;
    });
    setMatrixDirty(true);
  }, []);

  const handleMatrixSave = useCallback(async () => {
    const overrides = Object.values(pendingOverrides);
    if (overrides.length === 0) return;

    setMatrixSaving(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/indexing/role-agent-overrides/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: selectedEnvironment, overrides }),
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', `Saved ${data.count} role-agent override${data.count !== 1 ? 's' : ''}`);
        setPendingOverrides({});
        setMatrixDirty(false);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to save overrides');
      }
    } catch (err) {
      showMessage('error', 'Network error saving role-agent overrides');
    } finally {
      setMatrixSaving(false);
    }
  }, [pendingOverrides, selectedEnvironment, onRefresh]);

  const handleMatrixDiscard = useCallback(() => {
    setPendingOverrides({});
    setMatrixDirty(false);
  }, []);

  // Fetch on first render
  useEffect(() => {
    if (!agentAccess && !loading) {
      onRefresh();
    }
  }, [agentAccess, loading, onRefresh]);

  // Clear action message after 5s
  useEffect(() => {
    if (actionMessage) {
      const t = setTimeout(() => setActionMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [actionMessage]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
  };

  // Add single user to an agent's access list
  const handleAddUser = async (agentId: string) => {
    if (!addEmailInput.trim()) return;
    setActionLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/indexing/agent-access/${agentId}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: addEmailInput.trim(), accessLevel: 'allowed', notes: addNotes || '', role: addRole, environment: selectedEnvironment }),
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', `Added ${data.email || addEmailInput.trim()} as ${data.role || addRole}`);
        setAddEmailInput('');
        setAddNotes('');
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to add user');
      }
    } catch (err) {
      showMessage('error', 'Network error adding user');
    } finally {
      setActionLoading(false);
    }
  };

  // Remove user from an agent's access list
  const handleRemoveUser = async (agentId: string, email: string) => {
    if (!window.confirm(`Remove ${email} from ${agentId} access list?`)) return;
    setActionLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/indexing/agent-access/${agentId}/users/${encodeURIComponent(email)}?environment=${selectedEnvironment}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', data.message);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to remove user');
      }
    } catch (err) {
      showMessage('error', 'Network error removing user');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk import emails
  const handleBulkImport = async () => {
    if (!bulkAgentId || !bulkEmails.trim()) return;
    const emailList = bulkEmails
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e.includes('@'));
    
    if (emailList.length === 0) {
      showMessage('error', 'No valid emails found. Separate with newlines, commas, or semicolons.');
      return;
    }

    if (!window.confirm(`Import ${emailList.length} emails as ${bulkRole}s to ${bulkAgentId}?`)) return;

    setActionLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/indexing/agent-access/${bulkAgentId}/users/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: emailList, accessLevel: 'allowed', notes: bulkNotes || 'Bulk import', role: bulkRole, environment: selectedEnvironment }),
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', `${data.summary.succeeded} of ${data.summary.total} imported as ${bulkRole}s`);
        setBulkEmails('');
        setBulkNotes('');
        setBulkRole('user');
        setBulkAgentId(null);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Bulk import failed');
      }
    } catch (err) {
      showMessage('error', 'Network error during bulk import');
    } finally {
      setActionLoading(false);
    }
  };

  // Clear all access list entries for an agent
  const handleClearList = async (agentId: string, displayName: string) => {
    if (!window.confirm(`Clear ALL access list entries for ${displayName}? This cannot be undone.`)) return;
    setActionLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/indexing/agent-access/${agentId}/users/bulk?environment=${selectedEnvironment}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', data.message);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to clear list');
      }
    } catch (err) {
      showMessage('error', 'Network error clearing list');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Job Code Management ──────────────────────────────────────
  const handleAddJobCode = async (agentId: string) => {
    if (!newJobCode.trim()) return;
    setJobCodeLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/job-codes/${agentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newJobCode.trim(), title: newJobCodeTitle.trim() }),
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', data.message);
        setNewJobCode('');
        setNewJobCodeTitle('');
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to add job code');
      }
    } catch (err) {
      showMessage('error', 'Network error adding job code');
    } finally {
      setJobCodeLoading(false);
    }
  };

  const openBulkJobCodes = (agentId: string) => {
    setBulkJobCodeAgent(agentId);
    setBulkJobCodeRows([{ code: '', title: '' }]);
  };

  const updateBulkJobCodeRow = (index: number, field: 'code' | 'title', value: string) =>
    setBulkJobCodeRows(rows => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  // Rows copied from a spreadsheet arrive as one "code<TAB>title" line each: fill one
  // row per line. Split on the first tab (else the first comma) so titles keep theirs.
  const handleBulkJobCodePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!/[\n\t]/.test(text)) return;
    e.preventDefault();
    const pasted = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(line => {
      const cut = line.includes('\t') ? line.indexOf('\t') : line.indexOf(',');
      return cut === -1
        ? { code: line, title: '' }
        : { code: line.slice(0, cut).trim(), title: line.slice(cut + 1).trim() };
    });
    setBulkJobCodeRows(rows => [...rows.slice(0, index), ...pasted, ...rows.slice(index + 1).filter(r => r.code || r.title)]);
  };

  const handleBulkAddJobCodes = async (agentId: string) => {
    const codes = bulkJobCodeRows.map(r => ({ code: r.code.trim(), title: r.title.trim() })).filter(r => r.code);
    if (!codes.length) return;
    setJobCodeLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/job-codes/${agentId}/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes }),
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', data.message);
        setBulkJobCodeAgent(null);
        setBulkJobCodeRows([{ code: '', title: '' }]);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to add job codes');
      }
    } catch (err) {
      showMessage('error', 'Network error adding job codes');
    } finally {
      setJobCodeLoading(false);
    }
  };

  const handleRemoveJobCode = async (agentId: string, code: string) => {
    if (!window.confirm(`Remove job code ${code} from access rules?`)) return;
    setJobCodeLoading(true);
    try {
      const resp = await authFetch(`${API_BASE}/admin/job-codes/${agentId}/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (data.success) {
        showMessage('success', data.message);
        onRefresh();
      } else {
        showMessage('error', data.error || 'Failed to remove job code');
      }
    } catch (err) {
      showMessage('error', 'Network error removing job code');
    } finally {
      setJobCodeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="idx-loading">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading access data...</p>
      </div>
    );
  }

  if (!agentAccess) {
    return (
      <div className="idx-empty-state">
        <Users size={48} />
        <h3>No access data available</h3>
        <button className="idx-action-btn" onClick={onRefresh}>
          <RefreshCw size={16} />
          Load Access Data
        </button>
      </div>
    );
  }

  const totalAdmins = agentAccess.agents.reduce((sum, a) => sum + a.admins.length, 0);
  const totalUsers = agentAccess.agents.reduce((sum, a) => sum + a.users.length, 0);
  const totalEntraUsers = agentAccess.graphApiStatus?.uniqueUsers ?? 0;

  return (
    <div className="idx-users-tab">
      {/* Header */}
      <div className="idx-section-header">
        <h2><Shield size={24} /> Agent Access Management</h2>
        <div className="idx-header-controls">
          <button className="idx-refresh-btn" onClick={onRefresh}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Action feedback */}
      {actionMessage && (
        <div className={`idx-info-banner ${actionMessage.type === 'error' ? 'idx-error-banner' : 'idx-success-banner'}`}>
          {actionMessage.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <div><p>{actionMessage.text}</p></div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {bulkAgentId && (
        <div className="idx-bulk-import-overlay">
          <div className="idx-bulk-import-modal">
            <div className="idx-bulk-import-header">
              <h3>Bulk Import to {agentAccess.agents.find(a => a.agentId === bulkAgentId)?.displayName || bulkAgentId}</h3>
              <button onClick={() => setBulkAgentId(null)} className="idx-close-btn"><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--idx-text-muted)', margin: '0 0 12px' }}>
              Paste email addresses below, one per line (or comma/semicolon separated).
            </p>
            <textarea
              className="idx-bulk-textarea"
              placeholder="user1@unifirst.com&#10;user2@unifirst.com&#10;user3@unifirst.com"
              value={bulkEmails}
              onChange={(e) => setBulkEmails(e.target.value)}
              rows={10}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
              <select
                value={bulkRole}
                onChange={(e) => setBulkRole(e.target.value as 'user' | 'admin')}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--idx-border-strong)', fontSize: '0.85rem', background: 'var(--idx-bg-primary)' }}
              >
                <option value="user">Import as Users</option>
                <option value="admin">Import as Admins</option>
              </select>
              <input
                type="text"
                placeholder="Notes (optional, e.g. 'Searchlight Phase II')"
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--idx-border-strong)', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>
                {bulkEmails.split(/[\n,;]+/).filter(e => e.trim().includes('@')).length} valid emails detected
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="idx-refresh-btn" onClick={() => setBulkAgentId(null)}>Cancel</button>
                <button 
                  className="idx-action-btn" 
                  onClick={handleBulkImport}
                  disabled={actionLoading || !bulkEmails.trim()}
                >
                  {actionLoading ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                  Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Graph API Status Banner */}
      {agentAccess.graphApiStatus && !agentAccess.graphApiStatus.configured && !agentAccess.graphApiStatus.error && (
        <div className="idx-info-banner">
          <Info size={18} />
          <div>
            <strong>Entra ID Role Assignments Not Yet Available</strong>
            <p>
              To show users assigned via Azure Entra ID app roles, an IT request is needed for the 
              <code>{agentAccess.graphApiStatus.requiredPermission}</code> permission.
            </p>
            {agentAccess.graphApiStatus.itRequestPath && (
              <p className="idx-info-path">
                Draft request: <code>{agentAccess.graphApiStatus.itRequestPath}</code>
              </p>
            )}
          </div>
        </div>
      )}
      {agentAccess.graphApiStatus?.error && (
        <div className="idx-info-banner idx-error-banner">
          <AlertTriangle size={18} />
          <div>
            <strong>Entra ID Role Query Error</strong>
            <p>{agentAccess.graphApiStatus.error}</p>
          </div>
        </div>
      )}
      {agentAccess.graphApiStatus?.configured && (
        <div className="idx-info-banner idx-success-banner">
          <CheckCircle size={18} />
          <div>
            <strong>Entra ID Roles Connected</strong>
            <p>
              {agentAccess.graphApiStatus.uniqueUsers} users across {agentAccess.graphApiStatus.totalAssignments} role assignments
              ({agentAccess.graphApiStatus.rolesFound} roles) — via <strong>{agentAccess.graphApiStatus.servicePrincipal}</strong>
            </p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="idx-users-stats">
        <div className="idx-stat-card">
          <Shield size={24} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{agentAccess.superAdmins.length}</span>
            <span className="idx-stat-label">Super Admins</span>
          </div>
        </div>
        <div className="idx-stat-card">
          <UserCheck size={24} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{totalAdmins}</span>
            <span className="idx-stat-label">Agent Admins</span>
          </div>
        </div>
        <div className="idx-stat-card">
          <Users size={24} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">{totalUsers}</span>
            <span className="idx-stat-label">Access List Users</span>
          </div>
        </div>
        <div className="idx-stat-card" style={agentAccess.graphApiStatus?.configured ? { borderLeft: '3px solid #059669' } : undefined}>
          <Globe size={24} />
          <div className="idx-stat-info">
            <span className="idx-stat-value">
              {agentAccess.graphApiStatus?.configured
                ? totalEntraUsers
                : Object.keys(agentAccess.roleDefinitions).length}
            </span>
            <span className="idx-stat-label">
              {agentAccess.graphApiStatus?.configured ? 'Entra Users' : 'Entra Roles'}
            </span>
          </div>
        </div>
        {agentAccess.locationAccessRules && Object.keys(agentAccess.locationAccessRules).length > 0 && (
          <div className="idx-stat-card" style={{ borderLeft: '3px solid #16a34a' }}>
            <MapPin size={24} />
            <div className="idx-stat-info">
              <span className="idx-stat-value">
                {Object.values(agentAccess.locationAccessRules).reduce((sum, rule) => sum + rule.allowedLocations.length, 0)}
              </span>
              <span className="idx-stat-label">Location Rules</span>
            </div>
          </div>
        )}
        {agentAccess.jobCodeAccessRules && Object.keys(agentAccess.jobCodeAccessRules).length > 0 && (
          <div className="idx-stat-card" style={{ borderLeft: '3px solid #2563eb' }}>
            <Briefcase size={24} />
            <div className="idx-stat-info">
              <span className="idx-stat-value">
                {Object.values(agentAccess.jobCodeAccessRules).reduce((sum, rule) => sum + rule.allowedJobCodes.length, 0)}
              </span>
              <span className="idx-stat-label">Job Code Rules</span>
            </div>
          </div>
        )}
      </div>

      {/* Role → Agent Visibility Matrix */}
      {agentAccess.roleDefinitions && (
        <div className="idx-agent-access-card" style={{ marginBottom: '16px', borderLeft: `3px solid ${matrixDirty ? '#f59e0b' : '#0ea5e9'}` }}>
          <div
            className="idx-agent-access-header"
            onClick={() => setMatrixExpanded(prev => !prev)}
            style={{ cursor: 'pointer' }}
          >
            <div className="idx-agent-access-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Shield size={18} style={{ color: matrixDirty ? '#f59e0b' : '#0ea5e9' }} />
              <h4 style={{ margin: 0 }}>Role → Agent Visibility</h4>
              {matrixDirty && (
                <span style={{
                  fontSize: '0.72rem', padding: '2px 8px', borderRadius: '9999px',
                  background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontWeight: 600
                }}>
                  {Object.keys(pendingOverrides).length} unsaved change{Object.keys(pendingOverrides).length !== 1 ? 's' : ''}
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-faint, #9da2a8)' }}>
                saved to Azure Table — persists across deploys
              </span>
            </div>
            <ChevronDown size={20} className={`idx-expand-icon ${matrixExpanded ? 'rotated' : ''}`} />
          </div>

          {matrixExpanded && (() => {
            const roleDefs = agentAccess.roleDefinitions;

            const userRoles = Object.entries(roleDefs)
              .filter(([r]) => r.endsWith('-user') || r === 'erp-user')
              .map(([r, agents]) => ({ role: r, agents: agents.map((a: string) => a.toLowerCase()) }));

            const allAgents = agentAccess.agents.map(a => ({
              id: a.agentId.toLowerCase(),
              name: a.displayName,
              isDefault: !!a.isDefaultAgent,
              hasLocationAccess: !!a.locationAccess,
              locationAccessEnabled: !!a.locationAccess?.enabled,
              hasJobCodeAccess: !!a.jobCodeAccess,
              jobCodeAccessEnabled: !!a.jobCodeAccess?.enabled,
            }));

            const roleColumns = userRoles.map(r => r.role);

            const getEffectiveState = (role: string, agentId: string, baseGranted: boolean, isDefault: boolean): boolean => {
              const key = `${role}:${agentId}`;
              if (key in pendingOverrides) return pendingOverrides[key].granted;
              return baseGranted;
            };

            const isDirtyCell = (role: string, agentId: string): boolean => {
              return `${role}:${agentId}` in pendingOverrides;
            };

            return (
              <div style={{ padding: '0 16px 16px', overflowX: 'auto' }}>
                {/* Save / Discard toolbar */}
                {matrixDirty && (
                  <div style={{
                    display: 'flex', gap: '8px', marginBottom: '10px', padding: '8px 12px',
                    background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a',
                    alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <span style={{ fontSize: '0.82rem', color: '#92400e', fontWeight: 500 }}>
                      {Object.keys(pendingOverrides).length} pending override{Object.keys(pendingOverrides).length !== 1 ? 's' : ''} for <strong>{selectedEnvironment}</strong>
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMatrixDiscard(); }}
                        style={{
                          fontSize: '0.78rem', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer',
                          background: 'var(--idx-bg-primary, #fff)', color: 'var(--idx-text-muted, #6f747b)', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontWeight: 500
                        }}
                      >
                        Discard
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMatrixSave(); }}
                        disabled={matrixSaving}
                        style={{
                          fontSize: '0.78rem', padding: '5px 14px', borderRadius: '6px', cursor: 'pointer',
                          background: '#2563eb', color: '#fff', border: 'none', fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: '5px', opacity: matrixSaving ? 0.7 : 1
                        }}
                      >
                        {matrixSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        Save to {selectedEnvironment}
                      </button>
                    </div>
                  </div>
                )}

                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem',
                  minWidth: `${200 + roleColumns.length * 90}px`
                }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--idx-border, #e3e5e8)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 700, position: 'sticky', left: 0, background: 'var(--idx-bg-primary, #fff)', zIndex: 1 }}>
                        Agent
                      </th>
                      <th style={{
                        textAlign: 'center', padding: '8px 6px', fontWeight: 700, fontSize: '0.72rem',
                        background: '#f0fdf4', color: '#15803d', minWidth: '70px'
                      }}>
                        Default
                      </th>
                      {roleColumns.map(role => (
                        <th key={role} style={{
                          textAlign: 'center', padding: '8px 4px', fontWeight: 600, fontSize: '0.68rem',
                          whiteSpace: 'nowrap', minWidth: '80px', color: 'var(--idx-text-secondary, #3d4045)'
                        }}>
                          {role.replace(/-user$/, '').replace(/-/g, ' ')}
                        </th>
                      ))}
                      <th style={{
                        textAlign: 'center', padding: '8px 6px', fontWeight: 700, fontSize: '0.72rem',
                        background: '#fef3c7', color: '#92400e', minWidth: '70px'
                      }}>
                        super-admin
                      </th>
                      <th style={{ textAlign: 'center', padding: '8px 6px', fontWeight: 600, fontSize: '0.72rem', minWidth: '90px', color: 'var(--idx-text-muted, #6f747b)' }}>
                        Access Gates
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allAgents.map((agent, idx) => {
                      const bgColor = idx % 2 === 0 ? '#fff' : '#fafafa';
                      const defaultKey = `__default__:${agent.id}`;
                      const defaultState = getEffectiveState('__default__', agent.id, agent.isDefault, true);
                      const defaultDirty = isDirtyCell('__default__', agent.id);

                      return (
                        <tr key={agent.id} style={{ borderBottom: '1px solid #f1f2f4', background: bgColor }}>
                          <td style={{
                            padding: '7px 10px', fontWeight: 600, position: 'sticky', left: 0,
                            background: bgColor, zIndex: 1, whiteSpace: 'nowrap'
                          }}>
                            <span style={{
                              display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%',
                              background: getAgentColor(agent.id), marginRight: '6px', verticalAlign: 'middle'
                            }} />
                            {agent.name}
                          </td>
                          {/* Default column — editable checkbox */}
                          <td style={{
                            textAlign: 'center', padding: '4px',
                            background: defaultDirty ? '#fef9c3' : (defaultState ? '#f0fdf4' : undefined)
                          }}>
                            <input
                              type="checkbox"
                              checked={defaultState}
                              onChange={() => handleMatrixToggle('__default__', agent.id, defaultState, true)}
                              onClick={(e) => e.stopPropagation()}
                              title={`${agent.name} — default access for all users`}
                              style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#16a34a' }}
                            />
                          </td>
                          {/* Role columns — editable checkboxes */}
                          {roleColumns.map(role => {
                            const roleEntry = userRoles.find(r => r.role === role);
                            const baseExplicit = roleEntry?.agents.includes(agent.id) ?? false;
                            const effectiveGranted = getEffectiveState(role, agent.id, baseExplicit, false);
                            const cellDirty = isDirtyCell(role, agent.id);
                            const isInheritedDefault = defaultState && !effectiveGranted;

                            return (
                              <td key={role} style={{
                                textAlign: 'center', padding: '4px',
                                background: cellDirty ? '#fef9c3' : undefined
                              }}>
                                {defaultState ? (
                                  <span title="Inherited from Default — all users already have access" style={{ cursor: 'default' }}>
                                    <Check size={14} style={{ color: '#a3a3a3' }} />
                                  </span>
                                ) : (
                                  <input
                                    type="checkbox"
                                    checked={effectiveGranted}
                                    onChange={() => handleMatrixToggle(role, agent.id, effectiveGranted, false)}
                                    onClick={(e) => e.stopPropagation()}
                                    title={`${role} → ${agent.name}`}
                                    style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#2563eb' }}
                                  />
                                )}
                              </td>
                            );
                          })}
                          {/* Super-admin column — always on, not editable */}
                          <td style={{ textAlign: 'center', padding: '4px', background: '#fffbeb' }}>
                            <CheckCircle size={15} style={{ color: '#d97706' }} />
                          </td>
                          {/* Access gates */}
                          <td style={{ textAlign: 'center', padding: '4px' }}>
                            <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              {agent.hasLocationAccess && (
                                <span style={{
                                  fontSize: '0.62rem', padding: '1px 5px', borderRadius: '9999px', fontWeight: 600,
                                  background: agent.locationAccessEnabled ? '#dcfce7' : '#f1f2f4',
                                  color: agent.locationAccessEnabled ? '#15803d' : '#9da2a8',
                                  border: `1px solid ${agent.locationAccessEnabled ? '#bbf7d0' : '#e3e5e8'}`
                                }}>
                                  <MapPin size={8} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} />
                                  Location
                                </span>
                              )}
                              {agent.hasJobCodeAccess && (
                                <span style={{
                                  fontSize: '0.62rem', padding: '1px 5px', borderRadius: '9999px', fontWeight: 600,
                                  background: agent.jobCodeAccessEnabled ? '#dbeafe' : '#f1f2f4',
                                  color: agent.jobCodeAccessEnabled ? '#1d4ed8' : '#9da2a8',
                                  border: `1px solid ${agent.jobCodeAccessEnabled ? '#bfdbfe' : '#e3e5e8'}`
                                }}>
                                  <Briefcase size={8} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} />
                                  Job Code
                                </span>
                              )}
                              {!agent.hasLocationAccess && !agent.hasJobCodeAccess && (
                                <span style={{ color: '#cfd2d6', fontSize: '0.72rem' }}>—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Legend */}
                <div style={{ display: 'flex', gap: '16px', marginTop: '12px', flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--idx-text-muted, #6f747b)' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ display: 'inline-block', width: '13px', height: '13px', border: '2px solid #16a34a', borderRadius: '3px', background: '#f0fdf4' }} />
                    Default (all users)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ display: 'inline-block', width: '13px', height: '13px', border: '2px solid #2563eb', borderRadius: '3px', background: '#eff6ff' }} />
                    Explicit role grant
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Check size={13} style={{ color: '#a3a3a3' }} /> Inherited (default agent)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle size={13} style={{ color: '#d97706' }} /> Super-admin (all agents)
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ display: 'inline-block', width: '13px', height: '13px', borderRadius: '3px', background: '#fef9c3' }} />
                    Unsaved change
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Role Simulation (localhost only) */}
      {isLocalDev && (
        <div className="idx-agent-access-card" style={{ marginBottom: '16px', borderLeft: '3px solid #7c3aed' }}>
          <div
            className="idx-agent-access-header"
            onClick={() => setSimExpanded(prev => !prev)}
            style={{ cursor: 'pointer' }}
          >
            <div className="idx-agent-access-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FlaskConical size={18} style={{ color: '#7c3aed' }} />
              <h4 style={{ margin: 0 }}>Role Simulation</h4>
              {activeSimProfile && (
                <span style={{
                  fontSize: '0.72rem', padding: '2px 8px', borderRadius: '9999px',
                  background: '#f5f3ff', color: '#7c3aed', border: '1px solid #c4b5fd', fontWeight: 600
                }}>
                  {activeSimProfile.name}
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-faint, #9da2a8)', marginLeft: 'auto', marginRight: '8px' }}>
                localhost only
              </span>
            </div>
            <ChevronDown size={20} className={`idx-expand-icon ${simExpanded ? 'rotated' : ''}`} />
          </div>

          {simExpanded && (
            <div style={{ padding: '12px 16px' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--idx-text-muted, #6f747b)', margin: '0 0 12px' }}>
                Simulate a user role to test RBAC. Roles are sent to the backend — the real
                <code style={{ fontSize: '0.78rem', padding: '1px 4px', background: 'var(--idx-bg-tertiary, #f1f2f4)', borderRadius: '4px', margin: '0 3px' }}>
                  calculateAgentsFromRoles()
                </code>
                pipeline determines visible agents and Scout routing.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
                {Object.values(MOCK_PROFILES).map(profile => {
                  const isActive = activeSimProfile?.id === profile.id;
                  const nonAuthRoles = profile.roles.filter(r => r !== 'authenticated');
                  return (
                    <button
                      key={profile.id}
                      onClick={(e) => { e.stopPropagation(); handleSimProfileSelect(profile.id); }}
                      style={{
                        textAlign: 'left', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                        border: isActive ? '2px solid #7c3aed' : '1px solid #e3e5e8',
                        background: isActive ? '#f5f3ff' : '#fff',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{profile.name}</span>
                        {isActive && <Check size={16} style={{ color: '#7c3aed' }} />}
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted, #6f747b)', margin: '4px 0 6px' }}>
                        {profile.description}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                        {nonAuthRoles.length > 0 ? nonAuthRoles.map(role => (
                          <span key={role} style={{
                            fontSize: '0.68rem', padding: '1px 6px', borderRadius: '9999px', fontWeight: 600,
                            background: isActive ? '#ede9fe' : '#f1f2f4',
                            color: isActive ? '#6d28d9' : '#6f747b'
                          }}>
                            {role}
                          </span>
                        )) : (
                          <span style={{
                            fontSize: '0.68rem', padding: '1px 6px', borderRadius: '9999px',
                            background: 'var(--idx-bg-tertiary, #f1f2f4)', color: 'var(--idx-text-faint, #9da2a8)', fontStyle: 'italic'
                          }}>
                            defaults only
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
                {activeSimProfile && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSimDisable(); }}
                    style={{
                      fontSize: '0.8rem', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                      background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', fontWeight: 500
                    }}
                  >
                    Disable Simulation
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); window.location.reload(); }}
                  style={{
                    fontSize: '0.8rem', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
                    background: '#7c3aed', color: '#fff', border: 'none', fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: '5px'
                  }}
                >
                  <RefreshCw size={14} />
                  Refresh to Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Super Admins Section */}
      {agentAccess.superAdmins.length > 0 && (
        <div className="idx-super-admins-section">
          <h3><Shield size={18} /> Super Admins (Full Access)</h3>
          <div className="idx-user-list">
            {agentAccess.superAdmins.map((admin, idx) => (
              <div key={idx} className="idx-user-badge super-admin">
                <Mail size={14} />
                {admin.email}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent Access Cards */}
      <div className="idx-agent-access-list">
        {agentAccess.agents.map(agent => {
          const isExpanded = expandedAgent === agent.agentId;
          const agentColor = getAgentColor(agent.agentId);
          const entraUserCount = agent.totalEntraUsers ?? 0;
          const hasData = agent.admins.length > 0 || agent.users.length > 0 || entraUserCount > 0 || agent.entraRoles.length > 0;

          // Build a unified tagged user list: Entra users + in-app users
          type TaggedUser = { name: string; id?: string; email?: string; tag: string; source: 'entra' | 'in-app'; role?: 'user' | 'admin' };
          const taggedUsers: TaggedUser[] = [];
          const seenEntraIds = new Set<string>();

          if (agent.entraRoleAssignments && !agent.isDefaultAgent) {
            for (const [roleName, users] of Object.entries(agent.entraRoleAssignments)) {
              for (const u of users) {
                if (!seenEntraIds.has(u.id)) {
                  seenEntraIds.add(u.id);
                  taggedUsers.push({ name: u.name, id: u.id, tag: roleName, source: 'entra' });
                }
              }
            }
          }

          for (const a of agent.admins) {
            taggedUsers.push({ name: a.email, email: a.email, tag: 'in-app', source: 'in-app', role: 'admin' });
          }
          for (const u of agent.users) {
            taggedUsers.push({ name: u.email, email: u.email, tag: 'in-app', source: 'in-app', role: (u.role || 'user') as 'user' | 'admin' });
          }

          // Legacy allMembers for the add/edit table section
          const allMembers: AgentUser[] = [
            ...agent.admins.map(a => ({
              email: a.email,
              accessLevel: 'allowed',
              role: 'admin' as const,
              addedBy: a.assignedBy,
              addedAt: a.assignedAt,
              notes: ''
            })),
            ...agent.users.map(u => ({
              ...u,
              role: (u.role || 'user') as 'user' | 'admin'
            }))
          ];
          const filteredUsers = userFilter
            ? allMembers.filter(u => u.email.toLowerCase().includes(userFilter.toLowerCase()))
            : allMembers;

          return (
            <div 
              key={agent.agentId}
              className={`idx-agent-access-card ${isExpanded ? 'expanded' : ''}`}
              style={{ '--agent-color': agentColor } as React.CSSProperties}
            >
              <div 
                className="idx-agent-access-header"
                onClick={() => setExpandedAgent(isExpanded ? null : agent.agentId)}
              >
                <div className="idx-agent-access-info">
                  <span className="idx-agent-dot" style={{ backgroundColor: agentColor }} />
                  <h4>{agent.displayName}</h4>
                  <span className="idx-access-counts">
                    {(() => {
                      const totalUsers = entraUserCount + allMembers.length;
                      return totalUsers > 0 ? (
                        <span className="idx-count-badge admin">
                          <Users size={12} /> {totalUsers} user{totalUsers !== 1 ? 's' : ''}
                        </span>
                      ) : null;
                    })()}
                    {agent.isDefaultAgent && entraUserCount > 0 && (
                      <span className="idx-count-badge" style={{ background: '#dbeafe', color: '#1d4ed8' }}>
                        <Globe size={12} /> Default
                      </span>
                    )}
                    {!agent.isDefaultAgent && entraUserCount > 0 && (
                      <span className="idx-count-badge role">
                        <Globe size={12} /> {entraUserCount} Entra
                      </span>
                    )}
                    {agent.locationAccess && (
                      <span className="idx-count-badge" style={{ 
                        background: agent.locationAccess.enabled ? '#dcfce7' : '#f1f2f4',
                        color: agent.locationAccess.enabled ? '#15803d' : '#6f747b'
                      }}>
                        <MapPin size={12} /> {agent.locationAccess.allowedLocations.length} location{agent.locationAccess.allowedLocations.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {agent.jobCodeAccess && (
                      <span className="idx-count-badge" style={{ 
                        background: agent.jobCodeAccess.enabled ? '#dbeafe' : '#f1f2f4',
                        color: agent.jobCodeAccess.enabled ? '#1d4ed8' : '#6f747b'
                      }}>
                        <Briefcase size={12} /> {agent.jobCodeAccess.allowedJobCodes.length} job code{agent.jobCodeAccess.allowedJobCodes.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {!hasData && !agent.locationAccess && !agent.jobCodeAccess && (
                      <span className="idx-no-data">No custom access configured</span>
                    )}
                  </span>
                </div>
                <ChevronDown 
                  size={20} 
                  className={`idx-expand-icon ${isExpanded ? 'rotated' : ''}`}
                />
              </div>

              {isExpanded && (
                <div className="idx-agent-access-details">
                  {/* Default agent banner */}
                  {agent.isDefaultAgent && entraUserCount > 0 && (
                    <div style={{
                      padding: '8px 12px', borderRadius: '8px', marginBottom: '12px',
                      background: 'linear-gradient(135deg, #eff6ff 0%, var(--idx-bg-inset, #f0f9ff) 100%)',
                      border: '1px solid #bfdbfe', fontSize: '0.82rem', color: '#1e40af'
                    }}>
                      <span style={{
                        display: 'inline-block', padding: '1px 8px', borderRadius: '9999px',
                        background: '#2563eb', color: '#fff', fontSize: '0.72rem', fontWeight: 700, marginRight: '6px'
                      }}>DEFAULT</span>
                      All {entraUserCount} Entra ID users have access (any role grants access)
                    </div>
                  )}

                  {agent.entraRoleAssignmentsError && (
                    <p className="idx-info-text" style={{ marginBottom: '10px' }}>
                      <Info size={12} /> {agent.entraRoleAssignmentsError}
                    </p>
                  )}

                  {/* Access Rules (location / job codes) */}
                  {(agent.locationAccess || agent.jobCodeAccess) && (
                    <div style={{
                      display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px'
                    }}>
                      {agent.locationAccess && (
                        <div style={{
                          flex: '1 1 280px', padding: '10px 14px', borderRadius: '8px',
                          background: agent.locationAccess.enabled ? '#f0fdf4' : '#f8f9fa',
                          border: agent.locationAccess.enabled ? '1px solid #bbf7d0' : '1px solid #e3e5e8',
                          fontSize: '0.8rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontWeight: 600 }}>
                            <MapPin size={13} style={{ color: agent.locationAccess.enabled ? '#16a34a' : '#9da2a8' }} />
                            Location Filter
                            <span style={{
                              fontSize: '0.68rem', padding: '1px 6px', borderRadius: '9999px',
                              background: agent.locationAccess.enabled ? '#dcfce7' : '#f1f2f4',
                              color: agent.locationAccess.enabled ? '#15803d' : '#6f747b', fontWeight: 600
                            }}>{agent.locationAccess.enabled ? 'ON' : 'OFF'}</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {agent.locationAccess.allowedLocations.map((loc, idx) => (
                              <span key={idx} style={{
                                padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                                background: agent.locationAccess!.enabled ? '#dbeafe' : '#f1f2f4',
                                color: agent.locationAccess!.enabled ? '#1d4ed8' : '#6f747b',
                                border: `1px solid ${agent.locationAccess!.enabled ? '#93c5fd' : '#cfd2d6'}`
                              }}>Loc {loc}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {agent.jobCodeAccess && (
                        <div style={{
                          flex: '1 1 280px', padding: '10px 14px', borderRadius: '8px',
                          background: agent.jobCodeAccess.enabled ? '#eff6ff' : '#f8f9fa',
                          border: agent.jobCodeAccess.enabled ? '1px solid #bfdbfe' : '1px solid #e3e5e8',
                          fontSize: '0.8rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontWeight: 600 }}>
                            <Briefcase size={13} style={{ color: agent.jobCodeAccess.enabled ? '#2563eb' : '#9da2a8' }} />
                            Job Code Filter
                            <span style={{
                              fontSize: '0.68rem', padding: '1px 6px', borderRadius: '9999px',
                              background: agent.jobCodeAccess.enabled ? '#dbeafe' : '#f1f2f4',
                              color: agent.jobCodeAccess.enabled ? '#1d4ed8' : '#6f747b', fontWeight: 600
                            }}>{agent.jobCodeAccess.enabled ? 'ON' : 'OFF'}</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                            {agent.jobCodeAccess.allowedJobCodes.map((code, idx) => (
                              <span key={idx} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                                padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600,
                                background: agent.jobCodeAccess!.enabled ? '#dbeafe' : '#f1f2f4',
                                color: agent.jobCodeAccess!.enabled ? '#1d4ed8' : '#6f747b',
                                border: `1px solid ${agent.jobCodeAccess!.enabled ? '#93c5fd' : '#cfd2d6'}`
                              }}>
                                {code}
                                {agent.jobCodeAccess!.jobCodeTitles[code] && (
                                  <span style={{ fontWeight: 400, fontSize: '0.7rem' }}>({agent.jobCodeAccess!.jobCodeTitles[code]})</span>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRemoveJobCode(agent.agentId, code); }}
                                  disabled={jobCodeLoading}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
                                    padding: '0 0 0 2px', display: 'inline-flex', alignItems: 'center', opacity: 0.6
                                  }}
                                ><X size={10} /></button>
                              </span>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input type="text" placeholder="Code" value={newJobCode}
                              onChange={(e) => setNewJobCode(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddJobCode(agent.agentId)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '90px', padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.78rem' }}
                            />
                            <input type="text" placeholder="Title" value={newJobCodeTitle}
                              onChange={(e) => setNewJobCodeTitle(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddJobCode(agent.agentId)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ flex: 1, padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.78rem' }}
                            />
                            <button className="idx-action-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                              onClick={(e) => { e.stopPropagation(); handleAddJobCode(agent.agentId); }}
                              disabled={jobCodeLoading || !newJobCode.trim()}
                            >{jobCodeLoading ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add</button>
                          </div>
                          {bulkJobCodeAgent === agent.agentId ? (
                            <div onClick={(e) => e.stopPropagation()}
                              style={{ marginTop: '8px', padding: '8px', borderRadius: '6px', border: '1px solid var(--idx-border, #e3e5e8)', background: 'var(--idx-bg-secondary, #f8f9fa)' }}>
                              <div style={{ fontSize: '0.72rem', color: 'var(--idx-text-muted, #6f747b)', marginBottom: '6px' }}>
                                One job code per row. You can paste rows copied from a spreadsheet (code, then title).
                              </div>
                              {bulkJobCodeRows.map((row, idx) => (
                                <div key={idx} style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '4px' }}>
                                  <input type="text" placeholder="Code" value={row.code} aria-label={`Job code ${idx + 1}`}
                                    onChange={(e) => updateBulkJobCodeRow(idx, 'code', e.target.value)}
                                    onPaste={(e) => handleBulkJobCodePaste(idx, e)}
                                    style={{ width: '90px', padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.78rem', background: 'var(--idx-bg-primary, #fff)', color: 'var(--idx-text-primary, #2b2e32)' }} />
                                  <input type="text" placeholder="Title" value={row.title} aria-label={`Job title ${idx + 1}`}
                                    onChange={(e) => updateBulkJobCodeRow(idx, 'title', e.target.value)}
                                    onPaste={(e) => handleBulkJobCodePaste(idx, e)}
                                    style={{ flex: 1, padding: '3px 8px', borderRadius: '5px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.78rem', background: 'var(--idx-bg-primary, #fff)', color: 'var(--idx-text-primary, #2b2e32)' }} />
                                  <button type="button" aria-label="Remove row"
                                    onClick={() => setBulkJobCodeRows(rows => (rows.length > 1 ? rows.filter((_, i) => i !== idx) : [{ code: '', title: '' }]))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--idx-text-faint, #9da2a8)', padding: '2px', display: 'flex' }}
                                  ><X size={12} /></button>
                                </div>
                              ))}
                              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                                <button type="button" className="idx-action-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                                  onClick={() => setBulkJobCodeRows(rows => [...rows, { code: '', title: '' }])}
                                ><Plus size={11} /> Add row</button>
                                <button type="button" className="idx-action-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                                  onClick={() => handleBulkAddJobCodes(agent.agentId)}
                                  disabled={jobCodeLoading || bulkJobCodeCount === 0}
                                >{jobCodeLoading ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add {bulkJobCodeCount} code{bulkJobCodeCount === 1 ? '' : 's'}</button>
                                <button type="button" className="idx-action-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                                  onClick={() => setBulkJobCodeAgent(null)}
                                >Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button type="button"
                              onClick={(e) => { e.stopPropagation(); openBulkJobCodes(agent.agentId); }}
                              style={{ marginTop: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.72rem', color: 'var(--idx-text-link, #3b82f6)' }}
                            >+ Add several at once</button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Unified User List ─────────────────────────────── */}
                  <div className="idx-access-section">
                    {/* Toolbar: add user + sort + filter */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Users size={14} /> All Users
                        <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--idx-text-muted, #6f747b)' }}>
                          ({taggedUsers.length})
                        </span>
                      </h5>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button className="idx-action-btn" style={{ fontSize: '0.72rem', padding: '3px 8px' }}
                          onClick={(e) => { e.stopPropagation(); setBulkAgentId(agent.agentId); }}
                        ><Plus size={11} /> Bulk Import</button>
                        {allMembers.length > 0 && (
                          <button className="idx-refresh-btn" style={{ fontSize: '0.72rem', padding: '3px 8px', color: '#ef4444' }}
                            onClick={(e) => { e.stopPropagation(); handleClearList(agent.agentId, agent.displayName); }}
                          ><Trash2 size={11} /> Clear In-App</button>
                        )}
                      </div>
                    </div>

                    {/* Add single user inline */}
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <input type="email" placeholder="user@unifirst.com" value={addEmailInput}
                        onChange={(e) => setAddEmailInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddUser(agent.agentId)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ flex: 1, minWidth: '180px', padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.82rem' }}
                      />
                      <select value={addRole} onChange={(e) => setAddRole(e.target.value as 'user' | 'admin')}
                        onClick={(e) => e.stopPropagation()}
                        style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.82rem', background: 'white' }}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                      <input type="text" placeholder="Notes" value={addNotes}
                        onChange={(e) => setAddNotes(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '130px', padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.82rem' }}
                      />
                      <button className="idx-action-btn" style={{ fontSize: '0.78rem', padding: '5px 10px' }}
                        onClick={(e) => { e.stopPropagation(); handleAddUser(agent.agentId); }}
                        disabled={actionLoading || !addEmailInput.trim()}
                      ><Plus size={12} /> Add</button>
                    </div>

                    {/* Sort + filter bar */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {taggedUsers.length > 5 && (
                        <input type="text" placeholder="Filter..." value={userFilter}
                          onChange={(e) => setUserFilter(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '160px', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--idx-border-strong, #cfd2d6)', fontSize: '0.8rem' }}
                        />
                      )}
                      <div style={{ display: 'flex', gap: '2px', marginLeft: 'auto' }}>
                        {([['name', 'A-Z'], ['role', 'Role'], ['source', 'Source'], ['date', 'Date']] as const).map(([key, label]) => (
                          <button key={key}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                              else { setSortBy(key); setSortDir('asc'); }
                            }}
                            style={{
                              padding: '3px 8px', borderRadius: '5px', fontSize: '0.72rem', fontWeight: sortBy === key ? 600 : 400,
                              border: sortBy === key ? '1px solid #93c5fd' : '1px solid #e3e5e8',
                              background: sortBy === key ? '#eff6ff' : '#fff',
                              color: sortBy === key ? '#2563eb' : '#6f747b',
                              cursor: 'pointer'
                            }}
                          >{label}{sortBy === key && (sortDir === 'asc' ? ' ↑' : ' ↓')}</button>
                        ))}
                      </div>
                    </div>

                    {/* User table */}
                    {(() => {
                      const filtered = userFilter
                        ? taggedUsers.filter(u => (u.name || u.email || '').toLowerCase().includes(userFilter.toLowerCase()) || u.tag.toLowerCase().includes(userFilter.toLowerCase()))
                        : taggedUsers;

                      const sorted = [...filtered].sort((a, b) => {
                        let cmp = 0;
                        if (sortBy === 'name') cmp = (a.name || a.email || '').localeCompare(b.name || b.email || '');
                        else if (sortBy === 'role') cmp = a.tag.localeCompare(b.tag);
                        else if (sortBy === 'source') cmp = a.source.localeCompare(b.source);
                        else if (sortBy === 'date') {
                          const aDate = a.source === 'in-app' ? (allMembers.find(m => m.email === a.email)?.addedAt || '') : '';
                          const bDate = b.source === 'in-app' ? (allMembers.find(m => m.email === b.email)?.addedAt || '') : '';
                          cmp = aDate.localeCompare(bDate);
                        }
                        return sortDir === 'asc' ? cmp : -cmp;
                      });

                      if (sorted.length === 0 && taggedUsers.length === 0) {
                        return (
                          <p style={{ fontSize: '0.82rem', color: 'var(--idx-text-muted, #6f747b)', fontStyle: 'italic' }}>
                            No users configured. Add in-app users above or assign Entra roles.
                          </p>
                        );
                      }
                      if (sorted.length === 0) {
                        return <p style={{ fontSize: '0.82rem', color: 'var(--idx-text-muted, #6f747b)', fontStyle: 'italic' }}>No users match "{userFilter}"</p>;
                      }

                      const roleBadgeStyle = (tag: string, source: string): React.CSSProperties => {
                        if (source === 'entra') return { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' };
                        if (tag === 'in-app' && source === 'in-app') return { background: '#f1f2f4', color: '#6f747b', border: '1px solid #e3e5e8' };
                        return { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' };
                      };

                      return (
                        <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid var(--idx-border, #e3e5e8)', borderRadius: '8px' }}>
                          <table className="idx-access-table">
                            <thead>
                              <tr>
                                <th>Name / Email</th>
                                <th>Role</th>
                                <th>Source</th>
                                <th>Details</th>
                                <th style={{ width: '40px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {sorted.map((u, idx) => {
                                const inAppUser = u.source === 'in-app' ? allMembers.find(m => m.email === u.email) : null;
                                return (
                                  <tr key={`${u.source}-${u.id || u.email}-${idx}`}>
                                    <td style={{ fontSize: '0.82rem' }}>
                                      {u.source === 'entra' ? u.name : u.email}
                                    </td>
                                    <td>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                                        padding: '2px 7px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600,
                                        ...roleBadgeStyle(u.tag, u.source)
                                      }}>
                                        {u.role === 'admin' ? <Shield size={10} /> : <UserCheck size={10} />}
                                        {u.role === 'admin' ? 'Admin' : u.tag}
                                      </span>
                                    </td>
                                    <td>
                                      <span style={{
                                        fontSize: '0.68rem', padding: '1px 6px', borderRadius: '9999px', fontWeight: 600,
                                        background: u.source === 'entra' ? '#f0fdf4' : '#f1f2f4',
                                        color: u.source === 'entra' ? '#166534' : '#6f747b'
                                      }}>
                                        {u.source === 'entra' ? 'Entra ID' : 'In-App'}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: '0.78rem', color: 'var(--idx-text-muted, #6f747b)' }}>
                                      {inAppUser?.addedBy && <span>{inAppUser.addedBy}</span>}
                                      {inAppUser?.notes && <span> · {inAppUser.notes}</span>}
                                      {u.source === 'entra' && <span style={{ color: 'var(--idx-text-faint, #9da2a8)' }}>—</span>}
                                    </td>
                                    <td>
                                      {u.source === 'in-app' && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleRemoveUser(agent.agentId, u.email!); }}
                                          title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                                        ><Trash2 size={13} /></button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { UsersTab };
